import cls from 'classnames';
import { Image } from '@bfe/moly';
import { CoinInfo } from '@/types/common';
import { appPath } from '@web3/base-utils';
import styles from './index.module.less';

export interface PoolIcon {
  iconDayUrl: string;
  iconNightUrl: string;
}

// 基础props类型
export interface IconStrategyProps {
  projectIcon: string;
  poolIcons: PoolIcon[];
  baseStakeCoins: CoinInfo[];
}

// SOL链特殊显示
export const SOLIconStrategy = ({
  projectIcon,
  poolIcons,
}: IconStrategyProps) => {
  return (
    <div className={cls(styles.iconWrap)}>
      <span
        className={cls(
          'flex-shrink-0 flex-row-center flex-items-center',
          styles.iconToken,
          styles.size32
        )}
      >
        <Image
          className={cls(styles.iconTokenImg, styles.solIcon)}
          src={
            poolIcons?.[0]?.iconNightUrl ||
            `${appPath}/svg/home/icon-backup.svg`
          }
          style={{
            height: '100%',
            width: '100%',
          }}
          alt=""
        />
      </span>
      <Image
        src={projectIcon}
        className={cls('flex-shrink-0', styles.iconType)}
        style={{
          height: 16,
          width: 16,
        }}
        alt=""
      />
    </div>
  );
};

// METH特殊显示
export const METHIconStrategy = ({ projectIcon }: IconStrategyProps) => {
  return (
    <div className={cls(styles.iconWrap, styles.gridColumn2)}>
      <span
        key="isMETH"
        className={cls(
          'flex-shrink-0 flex-row-center flex-items-center',
          styles.iconToken,
          styles.size32
        )}
      >
        <Image
          className={cls(styles.iconTokenImg, styles.icon)}
          src="https://static.bymj.io/bhop/image/JT4kqJwAGZ3tk0Fhqe5YCHRTRsbKFzRB2jDaa01g_NQ.png"
          style={{
            height: '100%',
            width: '100%',
          }}
          alt=""
        />
        <Image
          src={projectIcon}
          className={cls('flex-shrink-0', styles.iconType)}
          style={{
            height: 16,
            width: 16,
          }}
          alt=""
        />
      </span>
    </div>
  );
};

// Lombard池子只显示第一个币种
export const LombardIconStrategy = ({
  projectIcon,
  poolIcons,
}: IconStrategyProps) => {
  // 确保有币种可以显示
  if (!poolIcons || poolIcons.length === 0) {
    return null;
  }

  const firstCoin = poolIcons[0];

  return (
    <div className={cls(styles.iconWrap)}>
      <span
        className={cls(
          'flex-shrink-0 flex-row-center flex-items-center',
          styles.iconToken,
          styles.size32
        )}
      >
        <Image
          className={styles.chainIcon}
          src={firstCoin.iconNightUrl || `${appPath}/svg/home/icon-backup.svg`}
          style={{
            height: '100%',
            width: '100%',
          }}
          alt=""
        />
        {projectIcon && (
          <Image
            src={projectIcon}
            className={cls('flex-shrink-0', styles.iconType)}
            style={{
              height: 16,
              width: 16,
            }}
            alt=""
          />
        )}
      </span>
    </div>
  );
};

// 默认多币种显示
export const DefaultIconStrategy = ({
  projectIcon,
  baseStakeCoins,
}: IconStrategyProps) => {
  return (
    <div
      className={cls(styles.iconWrap, {
        [styles.gridColumn3]: baseStakeCoins.length > 4,
        [styles.gridColumn2]: baseStakeCoins.length <= 4,
        [styles.smallIconWrap]: baseStakeCoins.length > 2,
      })}
    >
      {baseStakeCoins.map((item, index) => {
        return (
          <span
            key={item.contractAddress}
            className={cls(
              'flex-shrink-0 flex-row-center flex-items-center',
              styles.iconToken,
              styles.size32,
              {
                [styles.size24]: baseStakeCoins.length > 2,
                [styles.notFirst]:
                  index % (baseStakeCoins.length > 4 ? 3 : 2) !== 0,
                [styles.notFirstRow]:
                  index > (baseStakeCoins.length > 4 ? 2 : 1),
                [styles.notLast]: index === 3 && baseStakeCoins.length === 5,
              }
            )}
          >
            <Image
              className={styles.chainIcon}
              src={
                item.coinDetailJson?.iconNightUrl ||
                `${appPath}/svg/home/icon-backup.svg`
              }
              style={{
                height: '100%',
                width: '100%',
              }}
              alt=""
            />
            {projectIcon && index === baseStakeCoins.length - 1 && (
              <Image
                src={projectIcon}
                className={cls('flex-shrink-0', styles.iconType)}
                style={{
                  height: 16,
                  width: 16,
                }}
                alt=""
              />
            )}
          </span>
        );
      })}
    </div>
  );
};

// 策略选择器
export const getIconStrategy = ({
  poolCode,
  chainCode,
  poolName,
}: {
  poolCode: string;
  chainCode: string;
  poolName: string;
}) => {
  if (poolCode === 'METH') {
    return METHIconStrategy;
  }

  if (chainCode === 'SOL') {
    return SOLIconStrategy;
  }

  if (poolName === 'Lombard' || poolName === 'LBTC' || poolName === 'LBTCv') {
    return LombardIconStrategy;
  }

  return DefaultIconStrategy;
};
